import {
    HASH_INTERVAL_TICKS,
    PEER_TIMEOUT_MS,
    PROTOCOL_VERSION,
    TURN_HISTORY,
    TURN_LEAD,
    isTurnStart,
    turnOf,
    turnStartTick,
} from './constants';
import { decode, encode } from './protocol';
import type { NetMessage } from './protocol';
import { hashState } from '../sim/hash';
import type { Command, CommandDraft } from '../sim/commands';
import type { GameState } from '../sim/types';
import type { Transport } from './Transport';

/*
 * ליבת ה-lockstep.
 *
 * הרעיון כולו במשפט אחד: **הסימולציה זהה בשני הצדדים כי היא טהורה, ולכן מספיק
 * להעביר ברשת את הפקודות ולא את המצב.** מזה נגזר הכול — הטיק לא מתקדם עד שידועות
 * הפקודות של כולם לאותו תור, "אין לי פקודות" הוא מסר שחייבים לשלוח כמו כל מסר
 * אחר, וגיבוב תקופתי הוא הדרך היחידה לדעת שההנחה עדיין נכונה.
 *
 * **מה יושב כאן ומה לא:** כאן יושבת ההנהלה — תזמון תורות, חסימה, גיבוב וסטטוס.
 * `NetworkCommandSource` הוא רק המתאם ל-`GameLoop`, ו-`Transport` הוא רק צינור.
 * הסימולציה עצמה אינה יודעת שקיימת רשת, וזו כל הנקודה.
 *
 * **המפגש עם `GameLoop`:** הלולאה קוראת `ready(tick)` לפני כל טיק ו-`drain(tick)`
 * אחריו. השליחה יושבת ב-`drain` ולא בטיימר נפרד, ולכן היא כבולה לקצב הסימולציה —
 * וזה בדיוק מה שמונע מצד אחד לרוץ קדימה בלי גבול.
 */

export type SessionStatus =
    | { readonly kind: 'running' }
    /** ממתינים לפקודות של מישהו. המשחק קפוא, אבל בריא. */
    | { readonly kind: 'waiting'; readonly players: readonly number[] }
    /** המצבים התפצלו. אין טעם להמשיך — מכאן זה שני משחקים שונים. */
    | {
          readonly kind: 'desync';
          readonly tick: number;
          readonly player: number;
          readonly local: number;
          readonly remote: number;
      }
    /** מישהו לא ענה מספיק זמן. */
    | { readonly kind: 'peerLost'; readonly players: readonly number[] }
    | { readonly kind: 'ended'; readonly reason: string };

export interface SessionStats {
    readonly sentTurns: number;
    readonly receivedTurns: number;
    readonly receivedCommands: number;
    readonly sentHashes: number;
    readonly receivedHashes: number;
    readonly rejected: number;
}

export interface LockstepOptions {
    readonly transport: Transport;
    readonly localPlayer: number;
    /** כל מי שפקודותיו מגיעות ברשת. שחקן AI אינו כאן — הוא נגזר מהמצב בשני הצדדים. */
    readonly remotePlayers: readonly number[];
    /** קריאת המצב החי, לחישוב הגיבוב. אותו דפוס כמו ב-`AiCommandSource`. */
    readonly readState: () => GameState;
    /** שעון אמיתי לזיהוי פסק זמן. מוזרק כדי שטסטים יחזיקו אותו ביד. */
    readonly clock?: () => number;
    readonly onStatus?: (status: SessionStatus) => void;
}

export class LockstepSession {
    private readonly transport: Transport;
    private readonly localPlayer: number;
    private readonly remotePlayers: readonly number[];
    private readonly allPlayers: readonly number[];
    private readonly readState: () => GameState;
    private readonly clock: () => number;
    private readonly onStatus: ((status: SessionStatus) => void) | undefined;
    private readonly unsubscribe: () => void;

    /** פקודות שטרם נשלחו, לפי התור שאליו נסגרו. */
    private readonly outgoing = new Map<number, CommandDraft[]>();
    /** שחקן → תור → הפקודות שלו, כבר חתומות בטיק, בשחקן ובמונה. */
    private readonly turns = new Map<number, Map<number, Command[]>>();
    private readonly localHashes = new Map<number, number>();
    private readonly remoteHashes = new Map<number, Map<number, number>>();

    /*
     * התור האחרון שכבר נשלח. מתחיל ב-TURN_LEAD-1 ולא ב--1: התורות הראשונים ריקים
     * בהגדרה — אין להם ממי להישלח — וזה מה שמאפשר למשחק להתחיל לרוץ מיד.
     */
    private sentThrough = TURN_LEAD - 1;
    /** ‎-1 ולא 0: אפס הוא שעה חוקית, וכסימן ל"לא ממתינים" הוא היה מאפס את הפסק זמן בכל בדיקה. */
    private waitingSince = -1;
    private statusValue: SessionStatus = { kind: 'running' };
    private counters = {
        sentTurns: 0,
        receivedTurns: 0,
        receivedCommands: 0,
        sentHashes: 0,
        receivedHashes: 0,
        rejected: 0,
    };

    constructor(options: LockstepOptions) {
        this.transport = options.transport;
        this.localPlayer = options.localPlayer;
        this.remotePlayers = [...options.remotePlayers];
        this.allPlayers = [...new Set([options.localPlayer, ...options.remotePlayers])].sort(
            (a, b) => a - b,
        );
        this.readState = options.readState;
        this.clock = options.clock ?? (() => Date.now());
        this.onStatus = options.onStatus;
        this.unsubscribe = this.transport.onMessage((data) => this.handle(data));
    }

    get status(): SessionStatus {
        return this.statusValue;
    }

    get stats(): SessionStats {
        return { ...this.counters };
    }

    /** הגיבובים שחושבו מקומית, לפי טיק. נחשף לאבחון ולבדיקות. */
    get hashLog(): ReadonlyMap<number, number> {
        return this.localHashes;
    }

    /**
     * סוגר פקודה לתור עתידי ומחזיר את הטיק שבו היא תתבצע — בשני הצדדים.
     *
     * התור הוא **המאוחר מבין** "מספיק רחוק קדימה" ל"תור שעדיין לא נשלח": פקודה
     * שהייתה נכנסת לתור שכבר עזב את המכונה הייתה מתבצעת אצלנו ולא אצל היריב.
     */
    issue(draft: CommandDraft, currentTick: number): number {
        const turn = Math.max(turnOf(currentTick) + TURN_LEAD, this.sentThrough + 1);
        const bucket = this.outgoing.get(turn);
        if (bucket) bucket.push(draft);
        else this.outgoing.set(turn, [draft]);
        return turnStartTick(turn);
    }

    /**
     * האם מותר להריץ את הטיק.
     *
     * טיק שאינו תחילת תור אינו צורך שום דבר מהרשת — הפקודות של תור נכנסות כולן
     * בטיק הראשון שלו. לכן החסימה היא פעם בתור, לא פעם בטיק.
     */
    ready(tick: number): boolean {
        if (this.halted) return false;
        if (!isTurnStart(tick)) return true;

        const turn = turnOf(tick);
        if (turn < TURN_LEAD) return true;

        const missing = this.remotePlayers.filter((player) => !this.turns.get(player)?.has(turn));
        if (missing.length === 0) {
            this.stopWaiting();
            return true;
        }
        this.noteWaiting(missing);
        return false;
    }

    /** מוציא את פקודות הטיק, ותוך כדי גם שולח את מה שצריך להישלח. */
    drain(tick: number): Command[] {
        if (this.halted) return [];

        if (isTurnStart(tick)) this.flush(turnOf(tick));
        if (tick % HASH_INTERVAL_TICKS === 0) this.exchangeHash(tick);
        if (!isTurnStart(tick)) return [];

        const turn = turnOf(tick);
        const commands: Command[] = [];
        for (const player of this.allPlayers) {
            const forTurn = this.turns.get(player)?.get(turn);
            if (forTurn) commands.push(...forTurn);
        }
        this.forget(turn);
        return commands;
    }

    /** מודיע ליריב שיצאנו. לא סוגר את התעבורה — היא שייכת למי שיצר אותה. */
    leave(reason: string): void {
        this.transport.send(
            encode({
                kind: 'leave',
                protocol: PROTOCOL_VERSION,
                player: this.localPlayer,
                reason,
            }),
        );
        this.setStatus({ kind: 'ended', reason });
    }

    dispose(): void {
        this.unsubscribe();
    }

    private get halted(): boolean {
        const kind = this.statusValue.kind;
        return kind === 'desync' || kind === 'peerLost' || kind === 'ended';
    }

    /**
     * שולח כל תור שטרם נשלח עד `currentTurn + TURN_LEAD`.
     *
     * לולאה ולא שליחה בודדת, כי **חובה שיצא בדיוק מסר אחד לכל מספר תור**: חור
     * ברצף התורות הוא צד שני שממתין לנצח להודעה שלא תגיע.
     */
    private flush(currentTurn: number): void {
        const target = currentTurn + TURN_LEAD;
        for (let turn = this.sentThrough + 1; turn <= target; turn++) {
            const drafts = this.outgoing.get(turn) ?? [];
            this.outgoing.delete(turn);
            this.record(this.localPlayer, turn, drafts);
            this.transport.send(
                encode({
                    kind: 'turn',
                    protocol: PROTOCOL_VERSION,
                    player: this.localPlayer,
                    turn,
                    commands: drafts,
                }),
            );
            this.counters.sentTurns++;
        }
        if (target > this.sentThrough) this.sentThrough = target;
    }

    private exchangeHash(tick: number): void {
        const state = this.readState();
        // גיבוב של טיק אחר הוא רעש שנראה בדיוק כמו desync. עדיף לוותר עליו.
        if (state.tick !== tick) return;

        const hash = hashState(state);
        this.localHashes.set(tick, hash);
        this.transport.send(
            encode({
                kind: 'hash',
                protocol: PROTOCOL_VERSION,
                player: this.localPlayer,
                tick,
                hash,
            }),
        );
        this.counters.sentHashes++;
        this.compareHashes(tick);
    }

    private compareHashes(tick: number): void {
        const local = this.localHashes.get(tick);
        // גיבוב שהגיע לפני שהגענו לטיק שלו נשמר וממתין; אין מה להשוות עדיין.
        if (local === undefined) return;
        for (const player of this.remotePlayers) {
            const byTick = this.remoteHashes.get(player);
            const remote = byTick?.get(tick);
            if (remote === undefined) continue;
            if (remote !== local) {
                this.setStatus({ kind: 'desync', tick, player, local, remote });
                return;
            }
            // הושווה — אין סיבה להחזיק אותו. כך המפה מחזיקה רק את מה שעדיין ממתין.
            byTick?.delete(tick);
        }
    }

    private record(player: number, turn: number, drafts: readonly CommandDraft[]): void {
        let byTurn = this.turns.get(player);
        if (!byTurn) {
            byTurn = new Map();
            this.turns.set(player, byTurn);
        }
        // תור שכבר נקלט אינו נדרס: כפילות ברשת היא נורמלית, שכתוב הוא desync.
        if (byTurn.has(turn)) return;

        const tick = turnStartTick(turn);
        byTurn.set(
            turn,
            drafts.map((draft, seq) => ({ ...draft, tick, player, seq }) as Command),
        );
    }

    /**
     * מפנה תורות שכבר בוצעו, כדי שמשחק ארוך לא יצבור זיכרון.
     *
     * **יומן הגיבובים המקומי אינו מפונה בכוונה.** הוא מספר אחד לכל 5 שניות משחק —
     * משחק של שלוש שעות הוא 2,160 ערכים — והוא המקום היחיד שיודע לומר *באיזה טיק*
     * המשחקים התפצלו. זה בדיוק המידע שיהיה חסר כשיהיה desync אמיתי.
     */
    private forget(turn: number): void {
        const oldestTurn = turn - TURN_HISTORY;
        for (const byTurn of this.turns.values()) {
            for (const key of byTurn.keys()) if (key < oldestTurn) byTurn.delete(key);
        }
    }

    private handle(data: string): void {
        const result = decode(data);
        if (!result.ok) {
            // הודעה פגומה נספרת ונזרקת. אין טעם להפיל את המשחק בגלל רעש בודד.
            this.counters.rejected++;
            return;
        }
        const message: NetMessage = result.message;
        switch (message.kind) {
            case 'turn':
                if (message.player === this.localPlayer) return; // הד של עצמנו
                if (!this.remotePlayers.includes(message.player)) return;
                this.record(message.player, message.turn, message.commands);
                this.counters.receivedTurns++;
                this.counters.receivedCommands += message.commands.length;
                return;
            case 'hash': {
                if (message.player === this.localPlayer) return;
                let byTick = this.remoteHashes.get(message.player);
                if (!byTick) {
                    byTick = new Map();
                    this.remoteHashes.set(message.player, byTick);
                }
                byTick.set(message.tick, message.hash);
                this.counters.receivedHashes++;
                this.compareHashes(message.tick);
                return;
            }
            case 'leave':
                if (message.player === this.localPlayer) return;
                this.setStatus({ kind: 'peerLost', players: [message.player] });
                return;
            case 'join':
            case 'lobby':
            case 'start':
                /*
                 * שייכות ללובי (`net/RelayClient`). הסשן נוצר רק אחרי ש-`start`
                 * כבר סוכם, ולכן מבחינתו הן רעש — כולל `start` חוזר שה-relay שולח
                 * למי שהתחבר מחדש.
                 */
                return;
        }
    }

    private noteWaiting(players: readonly number[]): void {
        const now = this.clock();
        if (this.waitingSince < 0) this.waitingSince = now;
        if (now - this.waitingSince >= PEER_TIMEOUT_MS) {
            this.setStatus({ kind: 'peerLost', players: [...players] });
            return;
        }
        this.setStatus({ kind: 'waiting', players: [...players] });
    }

    private stopWaiting(): void {
        this.waitingSince = -1;
        if (this.statusValue.kind === 'waiting') this.setStatus({ kind: 'running' });
    }

    private setStatus(next: SessionStatus): void {
        // חתימה ולא השוואת אובייקטים: הסטטוס נבנה מחדש בכל בדיקה, וההודעה צריכה
        // לצאת רק כשהמשמעות השתנתה — אחרת המתנה של שנייה מציפה את הממשק.
        if (signature(next) === signature(this.statusValue)) return;
        this.statusValue = next;
        this.onStatus?.(next);
    }
}

function signature(status: SessionStatus): string {
    switch (status.kind) {
        case 'waiting':
        case 'peerLost':
            return `${status.kind}:${status.players.join(',')}`;
        case 'desync':
            return `desync:${status.tick}:${status.player}`;
        case 'ended':
            return `ended:${status.reason}`;
        default:
            return status.kind;
    }
}
